export interface HeaderPositionTotals {
  todaysPnl: number;
  totalPnl: number;
  costBasis: number;
}

export interface HeaderPortfolioTotals extends HeaderPositionTotals {
  /** Current account equity. Null when unavailable. */
  equity: number | null;
  /** Previous-close account equity (`last_equity`). Null when unavailable. */
  lastEquity: number | null;
  /** Marginable buying power. Null when unavailable. */
  buyingPower: number | null;
}

export interface HeaderPortfolioPercentages {
  todayPct: number | null;
  totalPct: number | null;
}

interface PositionTotals {
  cost_basis: string | null;
  unrealized_pl: string | null;
  unrealized_intraday_pl: string | null;
}

const parseFiniteNumber = (value: string | null): number => {
  if (value === null) {
    return 0;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

export const parseMarginAmount = (
  value: string | null | undefined,
): number | null => {
  if (value == null || value === "") {
    return null;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

export const sumPositionTotals = (
  positions: readonly PositionTotals[],
): HeaderPositionTotals =>
  positions.reduce<HeaderPositionTotals>(
    (totals, position) => ({
      todaysPnl:
        totals.todaysPnl + parseFiniteNumber(position.unrealized_intraday_pl),
      totalPnl: totals.totalPnl + parseFiniteNumber(position.unrealized_pl),
      costBasis: totals.costBasis + parseFiniteNumber(position.cost_basis),
    }),
    {
      todaysPnl: 0,
      totalPnl: 0,
      costBasis: 0,
    },
  );

export const deriveHeaderPortfolioPercentages = ({
  todaysPnl,
  totalPnl,
  costBasis,
  lastEquity,
}: HeaderPortfolioTotals): HeaderPortfolioPercentages => ({
  // Daily return is vs previous-close account equity, not signed current
  // position market value (cash and shorts skew / invert that denominator).
  todayPct:
    lastEquity === null || lastEquity === 0 ? null : todaysPnl / lastEquity,
  totalPct: costBasis === 0 ? null : totalPnl / costBasis,
});
